import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Search, X } from 'lucide-react';
import { Meta } from '@/components/seo/Meta';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { FadeIn, Stagger, StaggerItem } from '@/components/motion/FadeIn';
import { TextSplit, MaskReveal } from '@/components/motion/Reveal';
import { Skeleton } from '@/components/ui/Skeleton';
import { useProjects, useProjectCategories } from '@/lib/cms';
import type { Project } from '@/lib/cms-types';

const STATUSES: Array<{ value: 'all' | 'shipped' | 'ongoing' | 'concept'; label: string }> = [
  { value: 'all', label: 'All status' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'ongoing', label: 'Ongoing' },
  { value: 'concept', label: 'Concept' },
];

const SORTS = [
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' },
  { value: 'featured', label: 'Featured first' },
  { value: 'a-z', label: 'A–Z' },
] as const;

export function ProjectsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get('category') ?? 'all';
  const status = (searchParams.get('status') ?? 'all') as 'all' | 'shipped' | 'ongoing' | 'concept';
  const sort = (searchParams.get('sort') ?? 'newest') as 'newest' | 'oldest' | 'featured' | 'a-z';
  const search = searchParams.get('q') ?? '';

  const filters = useMemo(
    () => ({ category, status, sort, search }),
    [category, status, sort, search],
  );

  // Always render exactly what's in the DB — no fallback mock array.
  // Admin-created projects are the only source. When the DB is empty (or
  // filters don't match), the empty state below renders.
  const { data: projects = [], isLoading } = useProjects(filters);
  const { data: categories = [] } = useProjectCategories();
  const list = projects;

  function setParam(key: string, value: string | null) {
    if (!value || value === 'all' || value === '') {
      searchParams.delete(key);
    } else {
      searchParams.set(key, value);
    }
    setSearchParams(searchParams, { replace: true });
  }

  const hasActiveFilters = category !== 'all' || status !== 'all' || sort !== 'newest' || search.length > 0;

  return (
    <>
      <Meta
        title="Projects"
        description="Selected work from Vonlof — case studies across healthcare, finance, education, and government."
        path="/projects"
      />
      <main className="pt-24 pb-12 sm:pt-28 md:pt-32 md:pb-20">
        <MaskReveal className="container-wide" duration={1}>
          <FadeIn>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-500">Selected work</p>
          </FadeIn>
          <TextSplit
            text="Real problems, shipped solutions."
            className="mt-3 display-serif text-display-l text-fg"
            delay={0.1}
            stagger={0.06}
          />
          <FadeIn delay={0.35}>
            <p className="mt-6 max-w-2xl text-lg text-fg-muted">
              Case studies from across healthcare, finance, education, and government — selected for craft and outcome.
            </p>
          </FadeIn>

          {/* FILTERS */}
          <FadeIn delay={0.2}>
            <div className="mt-12 flex flex-col gap-4 rounded-xl border border-border bg-bg-elevated/40 p-4 md:flex-row md:items-center md:justify-between md:p-6">
              <div className="relative flex-1 md:max-w-sm">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-subtle" />
                <Input
                  type="search"
                  placeholder="Search projects…"
                  aria-label="Search projects"
                  value={search}
                  onChange={(e) => setParam('q', e.target.value)}
                  className="pl-9"
                />
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <label className="text-xs text-fg-subtle">
                  Sort
                  <select
                    value={sort}
                    onChange={(e) => setParam('sort', e.target.value)}
                    className="ml-2 rounded-md border border-border bg-bg-elevated px-2 py-1 text-sm text-fg"
                  >
                    {SORTS.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </label>
                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSearchParams(new URLSearchParams())}
                    leftIcon={<X className="h-3.5 w-3.5" />}
                  >
                    Reset
                  </Button>
                )}
              </div>
            </div>
          </FadeIn>

          {/* CATEGORY CHIPS */}
          <FadeIn delay={0.25}>
            <div
              className="-mx-4 mt-4 flex gap-2 overflow-x-auto px-4 pb-2 scrollbar-hide scroll-x-mask sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0 sm:[mask-image:none]"
              role="tablist"
              aria-label="Filter by category"
            >
              <button
                key="all"
                type="button"
                role="tab"
                aria-selected={category === 'all'}
                onClick={() => setParam('category', 'all')}
                className={
                  category === 'all'
                    ? 'rounded-full border border-gold-500/40 bg-gold-500/10 px-3 py-1.5 text-xs font-medium text-gold-500'
                    : 'rounded-full border border-border bg-bg-elevated px-3 py-1.5 text-xs font-medium text-fg-muted transition-colors hover:border-gold-500/40 hover:text-fg'
                }
              >
                All
              </button>
              {categories.map((c) => (
                <button
                  key={c.name}
                  type="button"
                  role="tab"
                  aria-selected={category === c.name}
                  onClick={() => setParam('category', c.name)}
                  className={
                    category === c.name
                      ? 'rounded-full border border-gold-500/40 bg-gold-500/10 px-3 py-1.5 text-xs font-medium text-gold-500'
                      : 'rounded-full border border-border bg-bg-elevated px-3 py-1.5 text-xs font-medium text-fg-muted transition-colors hover:border-gold-500/40 hover:text-fg'
                  }
                >
                  {c.name}
                </button>
              ))}
            </div>
          </FadeIn>

          {/* STATUS CHIPS */}
          <FadeIn delay={0.3}>
            <div className="mt-3 flex flex-wrap gap-2">
              {STATUSES.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => setParam('status', s.value)}
                  className={
                    status === s.value
                      ? 'rounded-md border border-gold-500/40 bg-gold-500/10 px-2.5 py-1 text-[10px] font-medium uppercase tracking-wider text-gold-500'
                      : 'rounded-md border border-border bg-transparent px-2.5 py-1 text-[10px] font-medium uppercase tracking-wider text-fg-subtle transition-colors hover:border-gold-500/40 hover:text-fg'
                  }
                >
                  {s.label}
                </button>
              ))}
            </div>
          </FadeIn>

          {/* RESULTS */}
          {isLoading ? (
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <Card key={i} className="h-full overflow-hidden">
                  <Skeleton className="aspect-video w-full" />
                  <CardHeader>
                    <Skeleton className="h-4 w-1/2" />
                    <Skeleton className="mt-2 h-5 w-3/4" />
                    <Skeleton className="mt-2 h-4 w-full" />
                  </CardHeader>
                </Card>
              ))}
            </div>
          ) : list.length === 0 ? (
            <Card className="mt-12 p-12 text-center">
              <p className="text-base font-semibold text-fg">No projects match those filters.</p>
              <p className="mt-2 text-sm text-fg-muted">Try a different category or clear the filters.</p>
              {hasActiveFilters && (
                <div className="mt-4">
                  <Button variant="secondary" onClick={() => setSearchParams(new URLSearchParams())}>
                    Reset filters
                  </Button>
                </div>
              )}
            </Card>
          ) : (
            <Stagger className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {list.map((p) => (
                <StaggerItem key={p.id}>
                  <ProjectCard project={p} />
                </StaggerItem>
              ))}
            </Stagger>
          )}

          {/* CTA */}
          <FadeIn delay={0.4}>
            <div className="mt-20 text-center">
              <p className="text-sm text-fg-muted">Have a project in mind?</p>
              <div className="mt-3">
                <Button asChild size="lg">
                  <Link to="/contact">
                    Tell us about it <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </FadeIn>
        </MaskReveal>
      </main>
    </>
  );
}

function ProjectCard({ project }: { project: Project }) {
  return (
    <Link to={`/projects/${project.slug}`} className="group block h-full">
      <Card className="h-full overflow-hidden transition-all hover:-translate-y-1 hover:border-gold-500/40 hover:shadow-gold">
        <div className="relative aspect-video w-full overflow-hidden bg-gradient-to-br from-gold-500/20 to-bg-elevated">
          {project.hero_image_url ? (
            <img
              src={project.hero_image_url}
              alt={`${project.title} hero image`}
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover transition-transform duration-slow group-hover:scale-105"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-xs uppercase tracking-widest text-fg-subtle">
              {project.category}
            </div>
          )}
          {project.is_featured && (
            <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-bg/70 px-2 py-1 text-[10px] font-medium uppercase tracking-wider text-gold-500 backdrop-blur-sm">
              Featured
            </span>
          )}
        </div>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="gold">{project.category}</Badge>
            <Badge>{labelForStatus(project.status)}</Badge>
            {project.year && <span className="text-xs text-fg-subtle">{project.year}</span>}
          </div>
          <CardTitle className="mt-3">{project.title}</CardTitle>
          {project.summary && <CardDescription className="line-clamp-2">{project.summary}</CardDescription>}
          {project.tech_stack.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1">
              {project.tech_stack.slice(0, 3).map((t) => (
                <span key={t} className="rounded-md bg-bg-subtle px-2 py-0.5 text-[10px] text-fg-muted">
                  {t}
                </span>
              ))}
              {project.tech_stack.length > 3 && (
                <span className="text-[10px] text-fg-subtle">+{project.tech_stack.length - 3}</span>
              )}
            </div>
          )}
          <span className="mt-4 inline-flex items-center gap-1 text-sm text-gold-500">
            Read case study
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </span>
        </CardHeader>
      </Card>
    </Link>
  );
}

function labelForStatus(status: Project['status']): string {
  switch (status) {
    case 'shipped':
      return 'Shipped';
    case 'ongoing':
      return 'Ongoing';
    case 'concept':
      return 'Concept';
  }
}